import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  DeleteOutline as DeleteIcon,
  DownloadOutlined as DownloadIcon,
  Inventory2Outlined as ItemsIcon,
  OpenInNewOutlined as OpenIcon
} from '@mui/icons-material';

// ---------------------------------------------------------------------------
// Constants (previously duplicated in both PartnerPortalPage and PartnerQuotesPage)
// ---------------------------------------------------------------------------
export const STATUS_OPTIONS = [
  ['submitted', 'Submitted'],
  ['under_review', 'Under review'],
  ['clarification_requested', 'Information requested'],
  ['revised', 'Revised'],
  ['accepted', 'Accepted'],
  ['rejected', 'Rejected'],
  ['closed', 'Closed']
];
export const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS);
export const STATUS_COLORS = {
  submitted: 'info',
  under_review: 'warning',
  clarification_requested: 'warning',
  revised: 'info',
  accepted: 'success',
  rejected: 'error',
  closed: 'default'
};
export const CLOSED_STATUSES = ['accepted', 'rejected', 'closed'];

export const ACCEPTED_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,.docx,.xlsx,.xls';
export const MAX_FILES = 5;
export const MAX_FILE_SIZE_MB = 15;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------
export const formatDate = (value) =>
  value ? new Date(value).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

export const formatMoney = (amount, currency) =>
  amount === null || amount === undefined
    ? null
    : `${currency || ''} ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`.trim();

export const createEmptyQuoteItem = () => ({ part_number: '', description: '', brand: '', quantity: '1', unit_price: '' });

export const calculateQuoteItemsTotal = (items = []) => items.reduce((total, item) => {
  const quantity = Number(item.quantity);
  const unitPrice = Number(item.unit_price);
  return total + (Number.isFinite(quantity) && quantity > 0 && Number.isFinite(unitPrice) && unitPrice >= 0 ? quantity * unitPrice : 0);
}, 0);

export const hasValidQuoteItems = (items = []) => items.some((item) => (
  Boolean(String(item.description || item.part_number || '').trim())
  && String(item.unit_price ?? '').trim() !== ''
  && Number(item.quantity) > 0
  && Number(item.unit_price) >= 0
));

export function QuoteItemsEditor({ items, onChange, currency = 'USD' }) {
  const updateItem = (index, field, value) => onChange(items.map((item, itemIndex) => (
    itemIndex === index ? { ...item, [field]: value } : item
  )));
  const addItem = () => onChange([...items, createEmptyQuoteItem()]);
  const removeItem = (index) => onChange(items.filter((_, itemIndex) => itemIndex !== index));
  const total = calculateQuoteItemsTotal(items);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: 'background.default' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
        <Box>
          <Typography fontWeight={900}>Quoted items</Typography>
          <Typography variant="caption" color="text.secondary">Capture one row per part, product, or service.</Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addItem}>Add item</Button>
      </Stack>
      {items.length === 0 ? (
        <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, border: '1px dashed', borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No structured items yet. Add items to show a clear commercial summary in the conversation.</Typography>
        </Box>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {items.map((item, index) => (
            <Box key={`quote-item-${index}`} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
                <Typography variant="caption" color="primary.main" fontWeight={900}>ITEM {index + 1}</Typography>
                <IconButton size="small" color="error" onClick={() => removeItem(index)} aria-label={`Remove item ${index + 1}`}><DeleteIcon fontSize="small" /></IconButton>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1.6fr 1fr .7fr 1fr' }, gap: 1.2 }}>
                <TextField size="small" label="Part number" value={item.part_number} onChange={(event) => updateItem(index, 'part_number', event.target.value)} />
                <TextField size="small" label="Description" value={item.description} onChange={(event) => updateItem(index, 'description', event.target.value)} required />
                <TextField size="small" label="Brand" value={item.brand} onChange={(event) => updateItem(index, 'brand', event.target.value)} />
                <TextField size="small" type="number" label="Qty" value={item.quantity} onChange={(event) => updateItem(index, 'quantity', event.target.value)} inputProps={{ min: .001, step: .001 }} />
                <TextField size="small" type="number" label={`Unit price (${currency})`} value={item.unit_price} onChange={(event) => updateItem(index, 'unit_price', event.target.value)} inputProps={{ min: 0, step: .01 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
                Line total: <Box component="span" sx={{ color: 'text.primary', fontWeight: 900 }}>{formatMoney((Number(item.quantity) || 0) * (Number(item.unit_price) || 0), currency)}</Box>
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
      <Divider sx={{ my: 1.5 }} />
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">Items total</Typography>
        <Typography fontWeight={900} color="primary.main">{formatMoney(total, currency)}</Typography>
      </Stack>
    </Paper>
  );
}

export function QuoteItemsSummary({ items = [], currency = 'USD' }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  const total = items.reduce((sum, item) => sum + Number(item.line_total || 0), 0);
  return (
    <>
      <Paper variant="outlined" sx={{ mx: { xs: 2, md: 3 }, mt: 1.5, borderRadius: 2.2, overflow: 'hidden', bgcolor: 'action.hover' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.2} sx={{ px: 1.5, py: 1 }}>
          <Box sx={{ width: 34, height: 34, flex: '0 0 auto', borderRadius: 1.8, display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <ItemsIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={900}>Quoted items</Typography>
            <Typography variant="caption" color="text.secondary">{items.length} line{items.length === 1 ? '' : 's'} · {formatMoney(total, currency)}</Typography>
          </Box>
          <Button size="small" variant="outlined" onClick={() => setOpen(true)} sx={{ flex: '0 0 auto', textTransform: 'none', fontWeight: 850 }}>
            View items
          </Button>
        </Stack>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={.5}>
            <Box><Typography variant="h6" fontWeight={900}>Quoted items</Typography><Typography variant="body2" color="text.secondary">Commercial detail for this conversation</Typography></Box>
            <Typography variant="h6" color="primary.main" fontWeight={900}>{formatMoney(total, currency)}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Stack spacing={1.2} sx={{ display: { xs: 'flex', sm: 'none' }, p: 1.5 }}>
            {items.map((item, index) => (
              <Paper key={item.id || item.line_number || index} variant="outlined" sx={{ p: 1.5, borderRadius: 2.2 }}>
                <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="caption" color="primary.main" fontWeight={900}>ITEM {index + 1}</Typography>
                  <Typography variant="subtitle2" fontWeight={900}>{formatMoney(item.line_total, currency)}</Typography>
                </Stack>
                <Typography variant="body2" fontWeight={850} sx={{ overflowWrap: 'anywhere' }}>
                  {item.part_number || 'No part number'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{item.description}</Typography>
                <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 1.2 }}>
                  {item.brand && <Typography variant="caption" color="text.secondary">Brand: {item.brand}</Typography>}
                  <Typography variant="caption" color="text.secondary">Qty: {Number(item.quantity).toLocaleString('en-US', { maximumFractionDigits: 3 })}</Typography>
                  <Typography variant="caption" color="text.secondary">Unit: {formatMoney(item.unit_price, currency)}</Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>
          <Box sx={{ display: { xs: 'none', sm: 'block' }, overflowX: 'auto', maxHeight: '68vh' }}>
            <Box sx={{ minWidth: 760 }}>
              <Box sx={{ position: 'sticky', top: 0, zIndex: 1, display: 'grid', gridTemplateColumns: '1.1fr 1.8fr 1fr .6fr 1fr 1fr', gap: 1, px: 2.5, py: 1.2, bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' }}>
                {['Part number', 'Description', 'Brand', 'Qty', 'Unit price', 'Line total'].map((label) => <Typography key={label} variant="caption" color="text.secondary" fontWeight={900}>{label}</Typography>)}
              </Box>
              {items.map((item) => (
                <Box key={item.id || item.line_number} sx={{ display: 'grid', gridTemplateColumns: '1.1fr 1.8fr 1fr .6fr 1fr 1fr', gap: 1, px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={800}>{item.part_number || '-'}</Typography>
                  <Typography variant="body2">{item.description}</Typography>
                  <Typography variant="body2" color="text.secondary">{item.brand || '-'}</Typography>
                  <Typography variant="body2">{Number(item.quantity).toLocaleString('en-US', { maximumFractionDigits: 3 })}</Typography>
                  <Typography variant="body2">{formatMoney(item.unit_price, currency)}</Typography>
                  <Typography variant="body2" fontWeight={900}>{formatMoney(item.line_total, currency)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="contained" onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Client-side file validation
// Previously the `accept` attribute on <input type="file"> was the ONLY
// gate — that's cosmetic and can be bypassed trivially (drag & drop, or
// just renaming a file). This does a real check before anything is queued
// for upload, and gives the user feedback instead of a silent server 400.
// ---------------------------------------------------------------------------
const ALLOWED_EXTENSIONS = ACCEPTED_FILE_TYPES.split(',');

export function validateFiles(fileList, existingCount = 0) {
  const incoming = Array.from(fileList || []);
  const errors = [];
  const accepted = [];

  if (incoming.length + existingCount > MAX_FILES) {
    errors.push(`You can attach up to ${MAX_FILES} files (you tried to add ${incoming.length + existingCount}).`);
    return { accepted, errors };
  }

  for (const file of incoming) {
    const extension = `.${file.name.split('.').pop().toLowerCase()}`;
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      errors.push(`${file.name}: unsupported file type.`);
      continue;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      errors.push(`${file.name}: exceeds ${MAX_FILE_SIZE_MB} MB.`);
      continue;
    }
    accepted.push(file);
  }
  return { accepted, errors };
}

// ---------------------------------------------------------------------------
// useAsyncAction — small hook to remove the repeated
// setLoading(true)/try/catch/finally boilerplate that was copy-pasted
// across ~6 handlers in each file.
// ---------------------------------------------------------------------------
export function useAsyncAction() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  const run = useCallback(async (fn, { fallbackMessage = 'Something went wrong.' } = {}) => {
    setPending(true);
    setError('');
    try {
      return await fn();
    } catch (requestError) {
      const message = requestError?.response?.data?.message || fallbackMessage;
      setError(message);
      throw requestError;
    } finally {
      setPending(false);
    }
  }, []);

  return { pending, error, setError, run };
}

// ---------------------------------------------------------------------------
// Shared message thread renderer — this markup (bubble alignment, colors,
// attached-file buttons) was duplicated almost verbatim between the
// partner-facing and internal views. `viewerType` just decides which
// sender is styled as "mine".
// ---------------------------------------------------------------------------
export function MessageThread({ messages, viewerType, onOpenFile, emptyLabel = 'No messages yet.' }) {
  const endRef = useRef(null);
  const messageCount = messages?.length || 0;
  const lastMessageId = messageCount ? messages[messageCount - 1].id : null;

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messageCount, lastMessageId]);

  if (!messages?.length) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
      {messages.map((message) => {
        const mine = message.sender_type === viewerType;
        return (
          <Box key={message.id} sx={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {message.author_name} · {formatDate(message.created_at)}
            </Typography>
            <Paper
              elevation={0}
              sx={{
                mt: 0.4,
                p: 1.6,
                borderRadius: mine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                bgcolor: mine ? 'primary.main' : 'background.paper',
                color: mine ? 'primary.contrastText' : 'text.primary',
                border: mine ? 0 : '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {message.message}
              </Typography>
              {message.files?.length > 0 && (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {message.files.map((file) => (
                    <Button
                      key={file.id}
                      size="small"
                      variant={mine ? 'text' : 'outlined'}
                      startIcon={<OpenIcon />}
                      onClick={() => onOpenFile(file)}
                      aria-label={`Open attached file ${file.original_name}`}
                      sx={{ justifyContent: 'flex-start', color: mine ? 'inherit' : 'primary.main', borderColor: mine ? 'transparent' : 'divider', textTransform: 'none' }}
                    >
                      {file.original_name}
                    </Button>
                  ))}
                </Stack>
              )}
            </Paper>
          </Box>
        );
      })}
      <Box ref={endRef} sx={{ height: 1 }} />
    </Stack>
  );
}

export function FilePreviewDialog({ open, loading, error, preview, onClose, onDownload }) {
  const [objectUrl, setObjectUrl] = useState('');

  useEffect(() => {
    if (!preview?.blob) {
      setObjectUrl('');
      return undefined;
    }
    const nextUrl = URL.createObjectURL(preview.blob);
    setObjectUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [preview]);

  const mimeType = String(preview?.mimeType || '').toLowerCase();
  const canPreviewImage = mimeType.startsWith('image/');
  const canPreviewPdf = mimeType === 'application/pdf';
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ fontWeight: 900, borderBottom: '1px solid', borderColor: 'divider' }}>
        {preview?.name || 'File preview'}
      </DialogTitle>
      <DialogContent sx={{ p: 0, minHeight: { xs: 360, md: 620 }, bgcolor: 'background.default', display: 'grid', placeItems: 'center' }}>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>
        ) : canPreviewImage && objectUrl ? (
          <Box component="img" src={objectUrl} alt={preview?.name || 'Attachment'} sx={{ display: 'block', maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', p: 2 }} />
        ) : canPreviewPdf && objectUrl ? (
          <Box component="iframe" src={objectUrl} title={preview?.name || 'PDF preview'} sx={{ width: '100%', height: { xs: 520, md: 700 }, border: 0, bgcolor: '#fff' }} />
        ) : (
          <Box sx={{ maxWidth: 560, p: 4, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={900}>Preview is not available for this file type</Typography>
            <Typography color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
              Word and Excel files cannot be rendered safely by the browser. The file has not been downloaded; use the download button only when you need the original document.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={onDownload} disabled={!preview?.blob || loading}>
          Download original
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Small attach-files button with built-in validation + selected-file chips.
// Replaces the raw <Button component="label"><input hidden .../></Button>
// pattern that had no validation and no accessible label.
// ---------------------------------------------------------------------------
export function FileAttachField({ files, onChange, disabled, existingCount = 0, onValidationError }) {
  const handleChange = (event) => {
    const { accepted, errors } = validateFiles(event.target.files, existingCount);
    if (errors.length) onValidationError?.(errors);
    onChange(accepted);
    // reset input so selecting the same file again re-triggers onChange
    event.target.value = '';
  };

  return (
    <Stack spacing={0.5}>
      <Button component="label" disabled={disabled} sx={{ alignSelf: 'flex-start' }}>
        Attach files
        <input
          hidden
          multiple
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          onChange={handleChange}
          aria-label="Attach files"
        />
      </Button>
      {files?.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {files.map((file) => file.name).join(', ')}
        </Typography>
      )}
    </Stack>
  );
}
